import type { EpisodicSeverityScale } from '@piccolohealth/pbs-common';
import {
  ChakraV3,
  DndKitCore,
  DndKitSortable,
  DragHandle,
  dataAttr,
  Sortable,
} from '@piccolohealth/ui';
import { P, uuid } from '@piccolohealth/util';
import React from 'react';
import { FaPlus } from 'react-icons/fa';

interface EpisodicSeverityScaleItemProps {
  scale: EpisodicSeverityScale;
  isActive?: boolean;
  onEdit: (description: string) => void;
  onRemove: () => void;
  onClick: () => void;
}

const EpisodicSeverityScaleItem = (props: EpisodicSeverityScaleItemProps) => {
  const { scale, isActive, onEdit, onRemove, onClick } = props;
  const [value, setValue] = React.useState(scale.description);

  const { attributes, setNodeRef, listeners, transform, transition } = DndKitSortable.useSortable({
    id: scale.id,
  });

  return (
    <ChakraV3.Box
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className='group'
      rounded='lg'
      px={2}
      _hover={{ bg: 'gray.100' }}
      _active={{ bg: 'gray.100' }}
      data-active={dataAttr(isActive)}
    >
      <Sortable ref={setNodeRef} transform={transform} transition={transition}>
        <ChakraV3.Editable.Root
          value={value}
          onValueChange={({ value }) => setValue(value)}
          onValueCommit={({ value }) => onEdit(value)}
          activationMode='none'
        >
          <ChakraV3.HStack w='full' h='full' gap={1}>
            <DragHandle listeners={listeners} attributes={attributes} size='xs' />
            <ChakraV3.HStack w='full' fontWeight='semibold'>
              <ChakraV3.Editable.Preview w='full' />
              <ChakraV3.Editable.Input />
              <ChakraV3.Spacer />
              <ChakraV3.EditableControls onRemove={onRemove} />
            </ChakraV3.HStack>
          </ChakraV3.HStack>
        </ChakraV3.Editable.Root>
      </Sortable>
    </ChakraV3.Box>
  );
};

interface Props {
  value: EpisodicSeverityScale[];
  onChange: (value: EpisodicSeverityScale[]) => void;
}

export const EpisodicSeverityScales = (props: Props) => {
  const { value, onChange } = props;

  const [activeValue, setActiveValue] = React.useState<string | null>(null);

  const sensors = DndKitCore.useSensors(
    DndKitCore.useSensor(DndKitCore.PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const onDragEnd = React.useCallback(
    async (event: DndKitCore.DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = value.findIndex((item) => item.id === active.id);
        const newIndex = value.findIndex((item) => item.id === over.id);

        if (oldIndex === newIndex) {
          return;
        }

        const fromScale = value[oldIndex];

        if (!fromScale) {
          return;
        }

        const reorderedScales = P.moveArrayItem(value, oldIndex, newIndex);

        onChange(reorderedScales);
      }
    },
    [onChange, value],
  );

  const onAdd = React.useCallback(() => {
    onChange([...value, { id: uuid(), description: `Level ${value.length + 1}` }]);
  }, [onChange, value]);

  const onEdit = React.useCallback(
    (id: string, description: string) => {
      onChange(
        value.map((v) => {
          if (v.id === id) {
            return { ...v, description };
          }
          return v;
        }),
      );
    },
    [onChange, value],
  );

  const onRemove = React.useCallback(
    (id: string) => {
      onChange(value.filter((v) => v.id !== id));
    },
    [onChange, value],
  );

  const onClick = React.useCallback((id: string) => {
    setActiveValue(id);
  }, []);

  return (
    <ChakraV3.Stack w='full' py={2}>
      <DndKitCore.DndContext
        sensors={sensors}
        collisionDetection={DndKitCore.closestCenter}
        onDragEnd={onDragEnd}
      >
        <DndKitSortable.SortableContext
          strategy={DndKitSortable.verticalListSortingStrategy}
          items={value.map(({ id }) => id)}
        >
          <ChakraV3.Stack gap={2}>
            {value.map((value) => (
              <EpisodicSeverityScaleItem
                key={value.id}
                scale={value}
                onEdit={(description) => onEdit(value.id, description)}
                onRemove={() => onRemove(value.id)}
                onClick={() => onClick(value.id)}
                isActive={value.id === activeValue}
              />
            ))}
          </ChakraV3.Stack>
        </DndKitSortable.SortableContext>
      </DndKitCore.DndContext>
      <ChakraV3.Button size='sm' onClick={onAdd} disabled={value.length === 10} variant='subtle'>
        <FaPlus />
        Add new scale
      </ChakraV3.Button>
    </ChakraV3.Stack>
  );
};
